// src/oura/components/TimelineReview.tsx
//
// The date diff, before anyone publishes (ourteam-frontend#3757, backend#4821).
//
// Luxe Dental's dates moved three times in nine days. The merchant had no way
// to see what moved, and neither did the person about to move them again: a
// publish was a button with no preview, so the diff was discovered in the
// merchant's notification.
//
// THE VERSION MOVES ONLY WHEN A DATE MOVES. Most plan edits move none — staff
// shuffling a line between months changes what a month CONTAINS, not when it
// lands — and a version that bumps on every save trains a merchant to ignore
// the notification exactly when it matters. So `datesMoved: 0` is a real and
// common answer, and the screen says it plainly rather than offering an empty
// diff and a button that would do nothing.

import { useEffect, useRef, useState } from "react";
import { AlertTriangleIcon, ArrowRightIcon } from "lucide-react";
import { PillButton, Surface, TYPE } from "@oura/ui";
import { cn } from "../lib/cn";
import { shortDate } from "../lib/oura";
import { useSnackbar } from "@hook/use-snackbar";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@component/dialog";
import {
    isTimelineFailure,
    publishTimeline,
    scheduleIsEmpty,
    timelinePublishable,
    type DateMove,
    type TimelinePayload,
} from "@service/aeo/clientReview";

/** The local day of an instant, as YYYY-MM-DD: just after midnight here is still the day before in UTC. */
function localDay(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** The card rule: a small blue label above the title, on the left. */
export function CardHead({ label, title, sub }: { label: string; title: string; sub?: string }) {
    return (
        <div>
            <p className={cn(TYPE.tc, "m-0 font-medium text-blue-600")}>{label}</p>
            <h2 className={cn(TYPE.tt, "m-0 mt-0.5 text-gray-900")}>{title}</h2>
            {sub && <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>{sub}</p>}
        </div>
    );
}

export function TimelineReview({
    businessId,
    payload,
    onPublished,
}: {
    businessId: string;
    payload: TimelinePayload;
    onPublished: () => void;
}) {
    const snackbar = useSnackbar();
    const [busy, setBusy] = useState(false);
    // Publish tells the client and writes a version that cannot be taken back, so it asks first.
    const [confirming, setConfirming] = useState(false);
    // A re-publish can leave the client untold (#4503): only for correcting our side when they already hold these dates. Never remembered.
    const [quiet, setQuiet] = useState(false);
    // "Open the timeline" on an onboarding Next card lands here with #publish: bring this card into view.
    const anchor = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (window.location.hash !== "#publish") return;
        // The month cards above still lay out after this card mounts: settle on it a few times, then stop.
        // With something to fix first, TimelineTab lists it right above this card: land there instead.
        const go = () => {
            const el = anchor.current;
            const target = conflicts.length > 0 && el?.previousElementSibling instanceof HTMLElement ? el.previousElementSibling : el;
            target?.scrollIntoView({ block: "start" });
        };
        const timers = [0, 300, 900].map((ms) => window.setTimeout(go, ms));
        return () => timers.forEach((t) => window.clearTimeout(t));
        // Once, on arrival.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [failure, setFailure] = useState<{ code: string | null; message: string } | null>(null);

    // `changePending` and the conflict list are the tab's to show (once, above
    // this card); this card is the diff and the button.
    const { published, diff, datesMoved, conflicts } = payload;
    // Every `from` null means there is no previous version to compare against:
    // this is the first schedule, not a list of changes from nothing.
    const firstPublish = published === null;
    // A schedule with nothing in any month is never sent, whatever else says yes.
    const empty = scheduleIsEmpty(payload);
    // A re-publish that moves no date writes no version and tells nobody: nothing to press (#4554).
    const nothingMoved = !firstPublish && datesMoved === 0;
    const canPublish = timelinePublishable(payload) && !nothingMoved;

    const publish = async () => {
        setBusy(true);
        setFailure(null);
        // `publishTimeline` never rejects: it catches every transport error itself and answers a
        // TimelineFailure (clientReview.ts), so `busy` and the dialog are always freed below.
        const silent = quiet && !firstPublish;
        const r = await publishTimeline(businessId, silent ? { notify: false } : undefined);
        setBusy(false);
        // Answered either way: the dialog has done its job, and a refusal reads in the card behind it.
        setConfirming(false);
        if (isTimelineFailure(r)) { setFailure(r); return; }
        // What really happened: a first version sends the whole schedule; a later one moves dates; nothing moved tells nobody.
        const n = r.datesMoved;
        const dates = `${n} ${n === 1 ? "date" : "dates"}`;
        if (!r.published) {
            snackbar.showSuccess("Nothing moved", r.message ?? "No date moved, so the published version is unchanged and the client was not told.");
        } else if (firstPublish) {
            snackbar.showSuccess("Schedule published", `Version ${r.version}, ${dates}. The client is told once.`);
        } else if (silent) {
            snackbar.showSuccess(`Published version ${r.version} quietly`, `${dates} moved. The client was not told.`);
        } else {
            snackbar.showSuccess(`Published version ${r.version}`, `${dates} moved. The client is told once.`);
        }
        onPublished();
    };

    const status = published
        ? `Version ${published.version} published by ${published.publishedBy} on ${shortDate(localDay(published.publishedAt))}.`
        : "Never published. The client has no dates yet.";

    return (
        <div ref={anchor} id="publish" className="scroll-mt-20">
        <Surface>
            <CardHead
                label="Publish"
                title={firstPublish ? "Publish the schedule" : "What would move if you published now"}
                sub={status}
            />
            <div className="mt-4 space-y-4">
                {empty ? (
                    // Nothing in any month: there is no schedule to send, so no count and no button that would send one.
                    <p className={cn(TYPE.tb, "m-0 rounded-xl bg-gray-50 px-3 py-2.5 text-gray-600")}>
                        Nothing is scheduled yet. Add the plan&apos;s lines under What was sold, then publish.
                    </p>
                ) : firstPublish ? (
                    // The first publish sends everything, and "everything" is the
                    // term and month-by-month cards right below. Listing all of it
                    // here as a diff from nothing (22 dates on The Lush Clinic)
                    // printed the whole schedule twice on one screen.
                    <p className={cn(TYPE.tb, "m-0 rounded-xl bg-gray-50 px-3 py-2.5 text-gray-600")}>
                        Publishing sends the client the whole schedule below:{" "}
                        <span className="font-semibold tabular-nums text-gray-900">{diff.length} {diff.length === 1 ? "date" : "dates"}</span>.
                    </p>
                ) : datesMoved === 0 ? (
                    // Said plainly, with no empty table underneath it. An empty
                    // diff plus an enabled button invites a publish that writes
                    // no version and tells nobody, which reads as a broken button.
                    <p className={cn(TYPE.tb, "m-0 rounded-xl bg-gray-50 px-3 py-2.5 text-gray-600")}>
                        No date has moved. Publishing would change nothing and the client would not be told.
                    </p>
                ) : (
                    <MoveList moves={diff} />
                )}

                {failure && (
                    <p className={cn(TYPE.tb, "m-0 flex items-start gap-2 rounded-xl bg-rose-50 px-3 py-2.5 text-rose-800")}>
                        <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 flex-shrink-0" />
                        {/* The server's own sentence: it is the thing that knows
                            why it refused. */}
                        {failure.message}
                    </p>
                )}

                <div className="flex flex-wrap items-center justify-end gap-3">
                    {!canPublish && (
                        // DISABLED, not a button that fails when pressed. A
                        // control that looks available and then refuses teaches
                        // people to press it twice.
                        <span className={cn(TYPE.tc, "text-gray-500")}>
                            {conflicts.length > 0
                                ? `Fix the ${conflicts.length === 1 ? "item" : `${conflicts.length} items`} above to publish.`
                                : empty
                                    ? "Nothing is scheduled yet."
                                    : nothingMoved
                                        ? "Nothing to publish: no date has moved."
                                        : "There is nothing to publish yet."}
                        </span>
                    )}
                    <PillButton variant="primary" busy={busy} disabled={!canPublish} onClick={() => { setQuiet(false); setConfirming(true); }}>
                        {firstPublish ? "Publish the schedule" : `Publish${datesMoved > 0 ? ` (${datesMoved})` : ""}`}
                    </PillButton>
                </div>
            </div>
        </Surface>
        <Dialog open={confirming} onOpenChange={(o) => { if (!o && !busy) setConfirming(false); }}>
            <DialogContent className="gap-0 p-6 sm:max-w-[460px] sm:rounded-2xl">
                <DialogTitle className="pr-8 text-lg font-semibold leading-snug text-gray-900">
                    {firstPublish ? "Publish the schedule?" : "Publish these dates?"}
                </DialogTitle>
                <DialogDescription asChild>
                    <div className={cn(TYPE.tb, "mt-2 space-y-2 text-gray-600")}>
                        <p className="m-0">
                            {firstPublish
                                ? <>The client will see the whole schedule: <span className="font-semibold tabular-nums text-gray-900">{diff.length} {diff.length === 1 ? "date" : "dates"}</span>.</>
                                : <>The client will see <span className="font-semibold tabular-nums text-gray-900">{datesMoved} {datesMoved === 1 ? "date" : "dates"}</span> moved.</>}
                            {quiet && !firstPublish ? " They are not told." : " They are told once."}
                        </p>
                        {!firstPublish && (
                            <fieldset className="m-0 space-y-1 border-0 p-0">
                                <legend className="sr-only">Tell the client?</legend>
                                {([
                                    { value: false, title: "Tell the client", sub: "A bell, an email and “Dates moved” on their Work page." },
                                    { value: true, title: "Don't tell the client", sub: "No bell, no email, and no “Dates moved” on their Work page. Use it only when the client already holds these dates, for example the Execution Timeline you sent." },
                                ] as const).map((o) => (
                                    <label
                                        key={o.title}
                                        className={cn("flex cursor-pointer gap-3 rounded-xl px-3 py-2 ring-1 ring-inset", quiet === o.value ? "bg-blue-50 ring-blue-200" : "ring-gray-200 hover:bg-gray-50", busy && "pointer-events-none opacity-60")}
                                    >
                                        <input
                                            type="radio"
                                            name="publish-tell"
                                            checked={quiet === o.value}
                                            disabled={busy}
                                            onChange={() => setQuiet(o.value)}
                                            className="mt-1 h-4 w-4 shrink-0 accent-blue-600"
                                        />
                                        <span className="min-w-0">
                                            <span className="block font-medium text-gray-900">{o.title}</span>
                                            <span className={cn(TYPE.tc, "block text-gray-500")}>{o.sub}</span>
                                        </span>
                                    </label>
                                ))}
                            </fieldset>
                        )}
                        <p className="m-0 text-gray-500">A publish cannot be taken back. A later change goes out as a new version.</p>
                    </div>
                </DialogDescription>
                <div className="mt-5 flex justify-end gap-2">
                    <PillButton size="sm" disabled={busy} onClick={() => setConfirming(false)}>Cancel</PillButton>
                    <PillButton size="sm" variant="primary" busy={busy} onClick={() => void publish()}>
                        {busy ? "Publishing…" : firstPublish ? "Publish the schedule" : quiet ? "Publish quietly" : "Publish"}
                    </PillButton>
                </div>
            </DialogContent>
        </Dialog>
        </div>
    );
}

/**
 * The dates that would move, as a small table: what, which month, was, now.
 * It used to be one run-on line per date, so the old and new dates never
 * lined up down the page and a reviewer could not scan the "now" column.
 */
function MoveList({ moves }: { moves: DateMove[] }) {
    if (moves.length === 0) {
        return <p className="text-sm text-gray-400">No dates to show yet.</p>;
    }
    return (
        <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                        <th className="w-px whitespace-nowrap px-3 py-2 text-center text-xs font-medium text-gray-500">Month</th>
                        <th className="w-px whitespace-nowrap px-3 py-2 text-center text-xs font-medium text-gray-500">Was</th>
                        <th className="w-px px-1 py-2" aria-hidden />
                        <th className="w-px whitespace-nowrap px-3 py-2 text-center text-xs font-medium text-gray-500">Now</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {moves.map((m, i) => (
                        <tr key={`${m.field}-${m.termMonth}-${i}`}>
                            {/* `label`, never the raw `field`. "report_date" is a
                                column name; "Report" is what a person reads. */}
                            <td className="px-3 py-2 text-gray-700">{m.label}</td>
                            <td className="px-3 py-2 text-center text-gray-500 tabular-nums">
                                {m.termMonth ?? <span className="text-gray-400">Term</span>}
                            </td>
                            {/* Struck through only when there IS an old date: a
                                line through "New" reads as a cancelled value. */}
                            <td className="whitespace-nowrap px-3 py-2 text-center text-gray-400 tabular-nums">
                                {m.from ? <span className="line-through">{shortDate(m.from)}</span> : "New"}
                            </td>
                            <td className="px-1 py-2 text-gray-300"><ArrowRightIcon aria-hidden className="h-3.5 w-3.5" /></td>
                            <td className="whitespace-nowrap px-3 py-2 text-center font-medium text-gray-900 tabular-nums">
                                {m.to ? shortDate(m.to) : "Removed"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
